import React from 'react'
import { isNil } from 'ramda'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import { IconButton, Badge } from '@material-ui/core'
import EqualizerIcon from '@material-ui/icons/Equalizer'
import CreateOutlinedIcon from '@material-ui/icons/CreateOutlined'
import QuestionAnswerOutlinedIcon from '@material-ui/icons/QuestionAnswerOutlined'

import AccessControl from 'app/auth/AccessControl'
import { ACTION_EDIT, ACTION_NEW, GENERAL_DIALOG } from 'app/constants/compose'
import { AccessType } from 'app/auth/accessControlService'
import PrimaryTooltip from 'app/componentV4/Tooltip/PrimaryTooltip'
import { useOpenCompose } from 'app/main/composeMessage/useOpenCompose'

import Unsend from 'app/main/messages/timeline/Action/Unsend'
import EndPoll from 'app/main/messages/timeline/Action/EndPoll'
import useAction from 'app/main/messages/timeline/Action/useAction'
import DeleteDraft from 'app/main/messages/timeline/Action/DeleteDraft'
import ViewInAction from 'app/main/messages/timeline/Action/ViewIn/ViewInAction'
import CancelUnsentEvent from 'app/main/messages/timeline/Action/CancelUnsentEvent'
import PublicLinkAction from 'app/main/messages/timeline/Action/PublicLink/PublicLinkAction'
import LibraryBooksOutlinedIcon from '@material-ui/icons/LibraryBooksOutlined'
import StarBorderOutlinedIcon from '@material-ui/icons/StarBorderOutlined'
import StarOutlinedIcon from '@material-ui/icons/StarOutlined'
import { UPDATED_MSG } from 'app/constants/messageDetail'

const useStyles = makeStyles((theme) => ({
  tooltip: {
    fontWeight: 'bolder',
    fontSize: '12px',
  },
  submitDialog: {
    color: theme.colors.primary,
    '& .submit-dialog': {
      width: '375px',
      '& .button-submit': {
        backgroundColor: theme.colors['red-500'],
        color: theme.palette.light.main,
        textTransform: 'none',
        padding: '0.75rem 1.25rem',
        '&:hover': {
          backgroundColor: theme.colors['red-600'],
        },
        '&:action': {
          backgroundColor: theme.colors['red-500'],
        },
      },
      '& .button-close': {
        textTransform: 'none',
        padding: '0.75rem 1.25rem',
        color: theme.colors['grey-200'],
        border: '1px solid',
        borderColor: theme.colors['grey-200'],
        borderRadius: '4px',
        backgroundColor: 'transparent !important',
        '&:hover': {
          color: theme.colors['grey-400'],
          borderColor: theme.colors['grey-400'],
        },
        '&:action': {
          color: theme.colors['grey-300'],
          borderColor: theme.colors['grey-300'],
        },
      },
    },
  },
}))

const StyledBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: theme.colors['primary-350'],
    color: theme.colors['primary'],
    fontSize: 9,
  },
}))(Badge)

const Actions = (props) => {
  const {
    id,
    isFavorite,
    isDisplayFavorite,
    isPublicLink,
    isUnsend,
    isPoll = false,
    isDraft = false,
    isViewIn = false,
    isDialog = false,
    isMetrics = false,
    isSharingEnabled = false,
    handleFetchTimeline,
    onFetchDetailLanguage,
    onOpenMetric,
    isEvent = false,
    isScheduled = false,
    scheduleCanceledDate,
    isReminder = false,
    isCommet = true,
    countComment = 0,
    onOpenComment = () => {},
    onSuccseAndRedirectPage,
    isFavoriteLoading,
    updateFavorites,
  } = props
  const { onOpenCompose } = useOpenCompose()
  // const tabView = Number(localStorage.getItem('timelineView')) ?? 0
  const classes = useStyles()
  const { formatMessage } = useIntl()

  const { cancelOrCancelEvent, deleteDraft, unsend, endPoll } =
    useAction(handleFetchTimeline)

  const handleEdit = () => {
    return (
      <PrimaryTooltip
        title={formatMessage({
          id: 'edit.label',
        })}
      >
        <IconButton
          aria-label="Edit Button Icon"
          size="small"
          className="text-secondary"
          onClick={() => {
            const onEditMessage = () => {
              handleFetchTimeline(id, UPDATED_MSG)
            }
            onOpenCompose(id, GENERAL_DIALOG, onEditMessage, ACTION_EDIT)
          }}
        >
          <CreateOutlinedIcon fontSize="small" />
        </IconButton>
      </PrimaryTooltip>
    )
  }

  const handleMetrics = () => {
    return (
      <PrimaryTooltip
        title={formatMessage({
          id: 'text.metrics',
        })}
      >
        <IconButton
          aria-label="Metrics Button Icon"
          size="small"
          className="text-primary"
          onClick={() => onOpenMetric(true)}
        >
          <EqualizerIcon fontSize="small" />
        </IconButton>
      </PrimaryTooltip>
    )
  }

  const handleComment = () => {
    return (
      <PrimaryTooltip
        title={formatMessage({
          id: 'notification.metrics.socialMediaInteractions.comments',
        })}
      >
        <IconButton
          aria-label="Metrics Button Icon"
          size="small"
          className="text-primary"
          onClick={() => onOpenComment(true)}
        >
          <StyledBadge
            badgeContent={countComment}
            invisible={countComment <= 0}
          >
            <QuestionAnswerOutlinedIcon fontSize="small" />
          </StyledBadge>
        </IconButton>
      </PrimaryTooltip>
    )
  }

  return (
    <>
      <div className="w-full h-full flex items-center space-x-5 justify-end">
        {isDisplayFavorite && (
          <PrimaryTooltip
            title={formatMessage({
              id: isFavorite ? 'action.remove.favorite' : 'action.add.favorite',
            })}
          >
            <IconButton
              className="text-primary"
              aria-haspopup="true"
              size="small"
              disabled={isFavoriteLoading}
              onClick={() => {
                updateFavorites(id, isFavorite)
              }}
            >
              {isFavorite ? (
                <StarOutlinedIcon fontSize="small" />
              ) : (
                <StarBorderOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </PrimaryTooltip>
        )}

        <AccessControl
          accesses={[AccessType.NOTIFICATION_SEND, AccessType.ADMINISTRATION]}
        >
          <div>
            <PrimaryTooltip
              title={formatMessage({
                id: 'timeline.action.make.copy',
              })}
            >
              <IconButton
                className="text-primary"
                aria-haspopup="true"
                size="small"
                onClick={() => {
                  onOpenCompose(
                    id,
                    GENERAL_DIALOG,
                    onSuccseAndRedirectPage,
                    ACTION_NEW,
                  )
                }}
              >
                <LibraryBooksOutlinedIcon fontSize="small" />
              </IconButton>
            </PrimaryTooltip>
          </div>
        </AccessControl>
        {isViewIn ? (
          <ViewInAction id={id} onFetchDetailLanguage={onFetchDetailLanguage} />
        ) : null}
        {isPublicLink ? (
          <PublicLinkAction id={id} isSharingEnabled={isSharingEnabled} />
        ) : null}
        {!isDraft && isCommet ? handleComment() : null}
        {isMetrics ? handleMetrics() : null}
        <AccessControl
          accesses={[AccessType.NOTIFICATION_SEND, AccessType.ADMINISTRATION]}
        >
          {isEvent ||
          isDraft ||
          (isScheduled && isNil(scheduleCanceledDate) && !isPoll)
            ? handleEdit()
            : null}
        </AccessControl>
        {!isDraft && !isScheduled && isEvent ? (
          <CancelUnsentEvent
            onSubmit={(type, reason) => cancelOrCancelEvent(id, type, reason)}
            onDialog={(value) => isDialog(value)}
          />
        ) : null}
        {isPoll && !isDraft && (
          <EndPoll
            onSubmit={() => endPoll(id)}
            className={classes.submitDialog}
            onDialog={(value) => isDialog(value)}
          />
        )}
        <AccessControl
          accesses={[
            AccessType.ADMINISTRATION,
            AccessType.NOTIFICATION_UNSENT_OWNED,
          ]}
        >
          {isUnsend ? (
            <Unsend
              onUnsend={() => unsend(id)}
              className={classes.submitDialog}
              onDialog={(value) => isDialog(value)}
              isReminder={isReminder}
            />
          ) : (
            <></>
          )}
        </AccessControl>

        {isDraft ? (
          <DeleteDraft
            onDelete={() => deleteDraft(id)}
            className={classes.submitDialog}
            onDialog={(value) => isDialog(value)}
          />
        ) : null}
      </div>
    </>
  )
}

Actions.propTypes = {
  id: PropTypes.number,
  isPoll: PropTypes.bool,
  isDraft: PropTypes.bool,
  isEvent: PropTypes.bool,
  isUnsend: PropTypes.bool,
  isViewIn: PropTypes.bool,
  isCommet: PropTypes.bool,
  isDialog: PropTypes.func,
  isMetrics: PropTypes.bool,
  isReminder: PropTypes.bool,
  isScheduled: PropTypes.bool,
  isFavorite: PropTypes.bool,
  isDisplayFavorite: PropTypes.bool,
  isFavoriteLoading: PropTypes.bool,
  recipients: PropTypes.object,
  isPublicLink: PropTypes.bool,
  onOpenMetric: PropTypes.func,
  onOpenComment: PropTypes.func,
  countComment: PropTypes.number,
  handleFetchTimeline: PropTypes.func,
  onSuccseAndRedirectPage: PropTypes.func,
  updateFavorites: PropTypes.func,
  onFetchDetailLanguage: PropTypes.func,
  scheduleCanceledDate: PropTypes.string,
}

export default Actions
